import React from 'react';
import { t } from '@lingui/macro';
import { noop } from 'utils/noop';
import {
    Button,
    Group,
    ColorMode,
    getClonedChild,
    Text,
    TextProps,
    ButtonProps,
    IconProps,
    Flex,
    Box,
    CloseOutlined,
    IconButtonProps,
    IconButton,
} from '@landr/maestro';
import * as Styled from './Coachmark.style';
import { CoachmarkVariantType, CoachmarkProps } from './Coachmark.types';

const Icon: React.FC<{
    icon: React.FC<IconProps>;
    variant?: CoachmarkVariantType;
}> = ({ icon: Icon, variant = 'base', ...props }) => {
    return (
        <Group.Item>
            <Styled.IconWrapper variant={variant}>
                <Icon />
            </Styled.IconWrapper>
        </Group.Item>
    );
};

Icon.displayName = 'Coachmark.Icon';

const Title: React.FC<TextProps> = ({
    children,
    ...props
}: React.PropsWithChildren<TextProps>) => (
    <Group.Item>
        <Text as="h3" fontWeight="bold" size="lg" {...props}>
            {children}
        </Text>
    </Group.Item>
);

Title.displayName = 'Coachmark.Title';

const Content: React.FC<TextProps> = ({
    children,
    ...props
}: React.PropsWithChildren<TextProps>) => (
    <Text as="p" size="md" {...props}>
        {children}
    </Text>
);

Content.displayName = 'Coachmark.Content';

interface ExitProps extends ButtonProps {
    onClick?: () => void;
}

const Exit: React.FC<ExitProps> = ({
    children,
    ...props
}: React.PropsWithChildren<ExitProps>) => {
    return (
        <Box paddingRight="md">
            <Button kind="minimal" variant="secondary" size="sm" {...props}>
                {children}
            </Button>
        </Box>
    );
};

Exit.displayName = 'Coachmark.Exit';

interface NextProps extends ButtonProps {
    coachmarkVariant?: CoachmarkVariantType;
    onClick?: () => void;
}

const Next: React.FC<NextProps> = ({
    children,
    coachmarkVariant,
    ...props
}: React.PropsWithChildren<NextProps>) => {
    const variant = coachmarkVariant === 'base' ? 'accent' : 'secondary';

    return (
        <Flex flexGrow="10" justifyContent="flex-end">
            <Button variant={variant} size="sm" {...props}>
                {children}
            </Button>
        </Flex>
    );
};

Next.displayName = 'Coachmark.Next';

interface CloseProps extends Partial<IconButtonProps> {
    coachmarkVariant?: CoachmarkVariantType;
    onClick?: () => void;
}

const Close: React.FC<CloseProps> = ({
    coachmarkVariant,
    label = t`Close`,
    size = 'md',
    icon = CloseOutlined,
    ...props
}) => {
    return (
        <Flex flexGrow="10" justifyContent="flex-end">
            <IconButton
                label={label}
                size={size}
                icon={icon}
                kind="link"
                variant="secondary"
                {...props}
            />
        </Flex>
    );
};

Close.displayName = 'Coachmark.Close';

interface CoachmarkSubComponents {
    Icon: typeof Icon;
    Title: typeof Title;
    Content: typeof Content;
    Exit: typeof Exit;
    Next: typeof Next;
    Close: typeof Close;
}

const Coachmark: React.FC<CoachmarkProps> & CoachmarkSubComponents = ({
    children,
    anchorRef,
    placement = 'auto',
    variant = 'base',
    onClickTarget = noop,
    onClose = noop,
    isOpen = true,
    ...props
}: React.PropsWithChildren<CoachmarkProps>) => {
    const childrenArray = React.Children.toArray(children);

    const offset: [number, number] = [12, 12];

    const icon = getClonedChild(childrenArray, 'Coachmark.Icon', {
        variant: variant,
    });
    const title = getClonedChild(childrenArray, 'Coachmark.Title');
    const content = getClonedChild(childrenArray, 'Coachmark.Content');
    const exit = getClonedChild(childrenArray, 'Coachmark.Exit', {
        coachmarkVariant: variant,
    });
    const next = getClonedChild(childrenArray, 'Coachmark.Next', {
        coachmarkVariant: variant,
    });
    const close = getClonedChild(childrenArray, 'Coachmark.Close', {
        coachmarkVariant: variant,
    });

    const mode = variant === 'base' ? 'invert' : 'dark';

    return (
        <ColorMode mode={mode}>
            <Styled.Coachmark
                anchorRef={anchorRef}
                isOpen={isOpen}
                hasArrow={true}
                placement={placement}
                offset={offset}
                variant={variant}
                onClose={onClose}
                {...props}
            >
                <Styled.Header>
                    <Styled.HeaderItem>
                        <Group
                            alignItems="flex-start"
                            justifyContent="flex-start"
                            gutter="xs"
                        >
                            {icon}
                            {title}
                        </Group>
                    </Styled.HeaderItem>
                    <Styled.HeaderItem flexGrow={1}>{close}</Styled.HeaderItem>
                </Styled.Header>
                {content}
                <Flex>
                    {exit}
                    {next}
                </Flex>
            </Styled.Coachmark>
        </ColorMode>
    );
};

Coachmark.Icon = Icon;
Coachmark.Title = Title;
Coachmark.Content = Content;
Coachmark.Exit = Exit;
Coachmark.Next = Next;
Coachmark.Close = Close;

export { Coachmark };
